import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CATEGORIES } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Users, Shield, Sparkles, Send, Search, CheckCircle, 
  ChevronRight, Zap, Target, DollarSign, Phone, Mail, 
  Clock, Briefcase, Award, GraduationCap, ArrowUpRight, MessageSquare, X, RefreshCw
} from 'lucide-react';

interface TalentCategory {
  id: string | number;
  name: string;
  categoryTitle: string;
  grade: string;
  nodes: string[];
  salary: string;
  fundingStatus: string;
  strategy: string;
  clearanceTime: string;
}

// Generate the 90 talent roles dynamically using the master CATEGORIES data
const generateTalentRoles = (): TalentCategory[] => {
  return CATEGORIES.map((cat) => {
    // Determine high-quality professional title based on category title
    let name = cat.title.replace(/\((.*?)\)/g, '').replace(/Category \d+:\s*/i, '').trim();
    // Beautify name to be a concrete job title
    if (name.includes("RESTORATION CORE")) name = "Restoration Core Engineer";
    else if (name.includes("IMPERIAL FORGE")) name = "NexoraX Industrial Metallurgist";
    else if (name.includes("AI SYSTEMS")) name = "Lead ML Systems Architect";
    else if (name.includes("RECONNAISSANCE")) name = "Strategic Intelligence Officer";
    else if (name.includes("INTELLIGENCE")) name = "Neural Intelligence Director";
    else if (name.includes("FRANCHISE COMMAND")) name = "Global Franchise Operations Lead";
    else if (name.includes("GROWTH")) name = "Strategic Market Growth Executive";
    else if (name.includes("NETWORK INFRASTRUCTURE")) name = "Edge Network Infrastructure Architect";
    else if (name.includes("CREATIVE")) name = "Chief Creative Brand Officer";
    else if (name.includes("NETWORK COMMAND")) name = "Mesh Network Operations Director";
    else if (name.includes("SYNDICATE HUB")) name = "Syndicate Operations Officer";
    else if (name.includes("NEXORAX QUANTUM MATRIX")) name = "Quantum Cryptography Lead";
    else if (name.includes("OMNIVERSE INTENT GRID")) name = "Omniverse Grid Scribe";
    else if (name.includes("NATIONAL GOVERNANCE")) name = "National Regulatory Compliance Architect";
    else if (name.includes("BUSINESS")) name = "Principal Business Strategist";
    else if (name.includes("EXPERIENCE CLOUD")) name = "Enterprise Experience Cloud Director";
    else if (name.includes("REVENUE OPS")) name = "Revenue Operations Commander";
    else if (name.includes("POS SYSTEMS")) name = "POS Systems Optimization Engineer";
    else if (name.includes("ACCOUNTING")) name = "Chronos Chief Financial Accountant";
    else if (name.includes("JUDICIARY & LAW")) name = "General Legal Counsel & Gavel Strike Marshal";
    else if (name.includes("TALENT HUB")) name = "Principal Private Secretary (Secure Invoice Register) Leader";
    else if (name.includes("NEXORAX INFRASTRUCTURE")) name = "NexoraX Systems Operations Lead";
    else if (name.includes("THE PHILANTHROPIC VAULT")) name = "Chief Trustee & Zakat DAO Scribe";
    else if (name.includes("WEALTH & NEXORAXTY")) name = "NexoraX Wealth Fund Custodian";
    else if (name.includes("BRANDING ENGINE")) name = "Vanguard Brand Integrity Auditor";
    else if (name.includes("CRM, DASHBOARD & SLIDE DECK")) name = "CRM Telemetry Analyst";
    else if (name.includes("AI AUTOMATION")) name = "Autonomous RPA Lead Implementer";
    else if (name.includes("QUANTUM COMPUTING")) name = "Quantum Superposition Engineer";
    else if (name.includes("BIOMETRICS & CYBERNETICS")) name = "Biometric Security Architect";
    else if (name.includes("PLANETARY LOGISTICS")) name = "Interstellar Cargo Logistics Lead";
    else if (name.includes("TACTICAL ESPIONAGE")) name = "Defensive Cyber-Countermeasures Officer";
    else if (name.includes("WEAPONIZED MARKETING")) name = "Cognitive Response Marketing Specialist";
    else if (name.includes("AUTOMATED DIPLOMACY")) name = "International Accord Smart-Scribe";
    else if (name.includes("ZERO-POINT COMMERCE")) name = "Zero-Point Arbitrage Expert";
    else if (name.includes("IMPERIAL COMMAND")) name = "Chief Systems Command Marshal";
    else if (name.includes("COMPETITION DESTROYER")) name = "Market Intercept & Interdiction Officer";
    else if (name.includes("COMPETITOR ANALYZER")) name = "Intellectual Capital Reconnaissance Specialist";
    else if (name.includes("MARKET STRATEGY")) name = "Macro-Economic Trend Modeler";
    else if (name.includes("CIVIL INFRASTRUCTURE")) name = "Urban Civil Twin Architect";
    else if (name.includes("THE STEWARDSHIP MATRIX")) name = "NexoraX Resources Stewardship Custodian";
    else if (name.includes("ROGUE ENTITY ELIMINATION")) name = "Brand Impersonation Interdiction Marshal";
    else if (name.includes("SPECTACLE COMMAND")) name = "Cinematic Event Production Specialist";
    else if (name.includes("MASS MEDIA & CONTENT")) name = "NexoraX Content Syndication Lead";
    else if (name.includes("LIQUIDITY EXPEDITION")) name = "High-Frequency Liquidity Merchant";
    else if (name.includes("QUANTUM DEFENSE")) name = "Cyber Penetration Strike Architect";
    else if (name.includes("GLOBAL FRANCHISE")) name = "Supply-Line Logistics Specialist";
    else if (name.includes("CIVIC URBAN PLANNING")) name = "Autonomous Urban Development Planner";
    else if (name.includes("REAL ESTATE ACQUISITION")) name = "NexoraX Domain & Property Scribe";
    else if (name.includes("CHRONOS TIME-BANKING")) name = "Chrono-Miter Resource Planner";
    else if (name.includes("NEXORAX EMPIRE COMMAND")) name = "Imperial Core Systems Administrator";
    else if (name.includes("COMMERCIAL GOVERNANCE")) name = "NexoraX Trade Regulation Officer";
    else if (name.includes("OMNIVERSE ASCENSION CORE")) name = "Omniverse Ascension Scriptor";
    else if (name.includes("NEXORAX SUITE")) name = "NexoraX Enterprise Suite Director";
    else if (name.includes("NexoraX Concierge")) name = "High-Ticket Client Closer Liaison";
    else if (name.includes("NexoraX Legacy Vault")) name = "Proof of Work Archival Officer";
    else if (name.includes("Autonomous Market Research Engine")) name = "Market Intelligence Research Architect";
    else if (name.includes("NexoraX finance & Zakat DAO")) name = "DAO Governance & Fiscal Treasurer";
    else if (name.includes("AI Asset Governance")) name = "AI Asset Seed Controller";
    else if (name.includes("Federated Network Sync")) name = "Mesh Synchronization Architect";
    else if (name.includes("The \"Beyond\" Neural Core")) name = "Beyond-State Systems Coordinator";
    else if (name.includes("Semantic Intelligence & Memetic Warfare")) name = "Memetic Influence Specialist";
    else if (name.includes("Autonomous Cinematic Forge")) name = "Cinematic Forge Video Architect";
    else if (name.includes("NexoraX Covenant & Legal Smart-Contracts")) name = "NexoraX eIDAS Smart-Contract Scribe";
    else if (name.includes("Transactional Identity & Mail Relay")) name = "NexoraX Mail Relay Admin";
    else if (name.includes("Edge-Mesh Traffic & Security Cloak")) name = "Cognitive Defense mesh Guardian";
    else if (name.includes("NexoraX Brand-Voice Synthesizer")) name = "Phonetic Synthesis Acoustic Engineer";
    else if (name.includes("Automated NexoraX Risk Modeler")) name = "Predictive Volatility Modeler";
    else if (name.includes("Zero-Latency Resource Allocator")) name = "NexoraX Bandwidth Coordinator";
    else if (name.includes("Synthetic Reality & Launch Simulator")) name = "Launch Propulsion Simulator Analyst";
    else if (name.includes("The Governance Neural Core")) name = "Master node Autonomics Controller";
    else if (name.includes("NexoraX Inbox Purifier")) name = "NexoraX Inbox Purifier Specialist";
    else if (name.includes("Chronos Time Management")) name = "Chronos Time Management Specialist";
    else if (name.includes("Master Roadmap Matrix")) name = "Master Roadmapping Director";
    else if (name.includes("No-Code Automation Webhook Streamliner")) name = "No-Code Streamlining Specialist";
    else if (name.includes("NexoraX Operational Workflows")) name = "Workspace Boards Integrator";
    else if (name.includes("NexoraX Revenue Cognition")) name = "Revenue Telemetry Engineer";
    else if (name.includes("NexoraX Cloud Workspace")) name = "NexoraX Storage Edifice Admin";
    else if (name.includes("NexoraX Synchronous Messaging")) name = "Messaging Intercept Specialist";
    else if (name.includes("NexoraX Ticketing Architecture")) name = "Help Desk Triage Dispatcher";
    else if (name.includes("NexoraX Asset Graphics")) name = "Semantic Intent Designer";
    else if (name.includes("HERMES ACQUISITION GRID")) name = "Hermes Acquisition Grid Commander";
    else if (name.includes("THE TRANSCENDENTAL NEXORAX CORE")) name = "Transcendental Systems Architect";
    else if (name.includes("THE OMEGA LOCK")) name = "Omega Lock Cryptographic Scriptor";
    else if (name.includes("THE NEXORAX ENGINE")) name = "NexoraX Program Engine Lead";
    else if (name.includes("THE NEXORAX GENERATOR")) name = "NexoraX Generator Administrator";
    else if (name.includes("THE NEXORAX ORACLE")) name = "NexoraX Oracle Intelligence Scribe";
    else if (name.includes("THE COGNITIVE DEFENSE MATRIX")) name = "Cognitive Defense Strike Architect";
    else if (name.includes("THE NEXORAX LEGACY ARCHIVIST")) name = "NexoraX Legacy Archival Scribe";
    else if (name.includes("NEXORAX PROGRAM ENGINE")) name = "NexoraX Program Technical Lead";
    else if (name.includes("NEXORAX QR ENGINE")) name = "NexoraX QR Integration Specialist";
    else {
      name = `NexoraX ${name} Professional`;
    }

    const mod = cat.id % 6;
    let grade = "Tier-One (Senior)";
    if (mod === 0) grade = "Singularity VIP Executive";
    else if (mod === 1) grade = "Principal Scribe";
    else if (mod === 2) grade = "Lead Systems Architect";
    else if (mod === 3) grade = "Elite Operative";
    else if (mod === 4) grade = "Sub-Node Commander";

    const baseSalaries = [115000, 140000, 185000, 220000, 310000, 450000];
    const salaryVal = baseSalaries[mod];
    const salaryStr = `£${salaryVal.toLocaleString()} per annum`;

    // Specialized Asset & Talent Placement Security States
    const fundingStates = [
      "100% SECURED // VIP Access Protocol Enabled",
      "100% COMMERCIALLY SECURED & MANAGED",
      "SECURE DEPLOYMENT PARTNER APPOINTED",
      "BUSINESS DEVELOPMENT PIPELINE VERIFIED",
      "ACTIVE SECURE SPONSORSHIP // SECURE GATEWAY ROUTING ACTIVE"
    ];
    const fundingStatus = fundingStates[cat.id % fundingStates.length];

    const strategies = [
      "Performs deep integration of proprietary AI infrastructure under 0.8s latency constraints. Maps incoming telemetry into secure gateway allocations.",
      "Optimizes transaction pipelines using VIP Access Protocol and local 10% Maintenance & Hosting Fee tracking. Pending accountant/legal review.",
      "Defends corporate IP NexoraXty, identifying rogue brand duplicates targeting NexoraX.com infrastructure using high-resolution node scans.",
      "Coordinates global expansion of franchise endpoints. Links point-of-sale telemetry securely to the Google Drive NexoraX Resource Library.",
      "Drives the 15-2-8 minute God-Mode Funnel (15m Trial, 2m Data Extraction, 8m Critical Closure) to secure high-ticket NexoraX clients.",
      "Directs secure operations for regional nodes, maintaining high-density synchronization workflows with the remote Pakistan Node."
    ];
    const strategy = strategies[cat.id % strategies.length];

    const clearanceTime = `${(cat.id % 7) + 2} Business Days`;

    return {
      id: cat.id,
      name,
      categoryTitle: cat.title,
      grade,
      nodes: cat.nodes || [],
      salary: salaryStr,
      fundingStatus,
      strategy,
      clearanceTime
    };
  });
};

export const NexoraXTalentHub: React.FC = () => {
  const [activeTalentId, setActiveTalentId] = useState<number>(3); // Default to ML Engineer / AI Systems
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState<string>('ALL');
  
  // Custom Chat Widget State
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ sender: 'user' | 'core'; text: string; time: string }>>([
    { 
      sender: 'core', 
      text: "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. NexoraX NexoraX Talent Acquisition Node active. Enter Candidate query or reference National Insurance Secure Credential Stored Server-Side.", 
      time: 'SYSTEM START' 
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const talentList = useMemo(() => generateTalentRoles(), []);

  // Filter talents
  const filteredTalents = useMemo(() => {
    return talentList.filter(talent => {
      const matchSearch = 
        talent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        talent.categoryTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        talent.nodes.some(n => n.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchGrade = gradeFilter === 'ALL' || talent.grade.includes(gradeFilter);
      return matchSearch && matchGrade;
    });
  }, [talentList, searchTerm, gradeFilter]);

  const activeTalent = useMemo(() => {
    return talentList.find(t => t.id === activeTalentId) || talentList[0];
  }, [talentList, activeTalentId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isTyping]);

  const handleSendChat = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!chatInput.trim()) return;

    const userText = chatInput;
    setChatInput('');
    
    // Add User Message
    const timestamp = new Date().toLocaleTimeString();
    setChatMessages(prev => [...prev, { sender: 'user', text: userText, time: timestamp }]);

    // Simulate AI Response
    setIsTyping(true);
    setTimeout(() => {
      let coreReply = "";
      const lower = userText.toLowerCase();

      if (lower.includes('NexoraX billing records') || lower.includes('atw') || lower.includes('atw_ref')) {
        coreReply = "MANDATE SECURITY HANDSHAKE SECURED: Specialised Business Onboarding and VIP Access Protocol is prioritised at 100% allocation. All setup costs are mapped under our direct corporate alignment structures and No-Win-No-Fee model.";
      } else if (lower.includes('director') || lower.includes('Director') || lower.includes('mahmood')) {
        coreReply = "SYSTEM RESPONSE // COMMAND DIRECTIVE: All strategic candidate workflows are mirrored securely to Chrome NexoraX Resource Library on Google Drive under CloudSync-02. Current target interviews coordinated directly on Director's private voice frequency: Private Data Hidden.";
      } else if (lower.includes('salary') || lower.includes('fee') || lower.includes('tax') || lower.includes('levy')) {
        coreReply = "FISCAL LEDGER ALIGNMENT: Standard client payment operations utilize our Secure Payment Portal. We apply the 10% Maintenance & Hosting Fee for ongoing hosted services. Eligible transactions may support 3% Community Allocation to our nominated charity partners, pending accountant/legal review.";
      } else {
        coreReply = `COGNITIVE ASSISTANT FEEDBACK: Candidate optimization protocols analyzing Node ${activeTalent.id} (${activeTalent.name}). Strategy aligned to ${activeTalent.grade} standard with expected clearance in ${activeTalent.clearanceTime}. NexoraX clearance parameters locked.`;
      }

      setChatMessages(prev => [...prev, { sender: 'core', text: coreReply, time: new Date().toLocaleTimeString() }]);
      setIsTyping(false);
    }, 1000);
  };

  const handleTriggerDirectInterview = (talent: TalentCategory) => {
    if (typeof (window as any).triggerNexoraXPayment === 'function') {
      (window as any).triggerNexoraXPayment({
        amount: 1499,
        clientName: `Talent Placement Mandate - Node ${talent.id}`,
        invoiceRef: `NX-TALENT-NODE${talent.id}-${Math.floor(1000 + Math.random() * 9000)}`,
        channel: 'SecurePayment'
      });
    } else {
      alert(`NexoraX Interview Token registered for ${talent.name}. To schedule, contact Executive Support at support@nexoraxdigital.co.uk or dial freephone 08000 622827.`);
    }
  };

  return (
    <div id="NexoraX-talent-hub-root" className="w-full bg-black min-h-[85vh] text-slate-100 rounded-3xl border border-slate-800 p-6 md:p-8 relative overflow-hidden flex flex-col md:flex-row gap-8">
      
      {/* Background cybernetic grid lines */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-35 pointer-events-none"></div>

      {/* Side Panel: Job Search & Sidebar List */}
      <div className="w-full md:w-80 flex flex-col space-y-4 shrink-0 relative z-10 border-b md:border-b-0 md:border-r border-slate-800/80 pr-0 md:pr-6 pb-6 md:pb-0">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#f59e0b] shadow-[0_0_10px_rgba(245,158,11,0.2)]" />
            <h3 className="text-sm font-sans font-black tracking-widest text-[#f59e0b] uppercase">TALENT ON-DEMAND</h3>
          </div>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            COGNITIVE DEPLOYMENTS (1-90 Tiers)
          </p>
        </div>

        {/* Search */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-lg blur opacity-25 group-hover:opacity-60 transition"></div>
          <div className="relative bg-black border border-slate-800 rounded-lg flex items-center px-2.5 py-1.5 shadow-md">
            <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
            <input 
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search roles or tags..."
              className="w-full bg-transparent text-slate-200 font-mono text-xs focus:outline-none placeholder-slate-600"
            />
          </div>
        </div>

        {/* Grade Filter Dropdown */}
        <div className="flex gap-1 bg-slate-950 p-1 border border-slate-900 rounded-lg">
          {['ALL', 'Senior', 'Lead', 'Executive', 'Operative'].map((tier) => {
            const isActive = gradeFilter === tier;
            return (
              <button
                key={tier}
                onClick={() => setGradeFilter(tier)}
                className={`flex-1 py-1 text-[8px] font-mono uppercase tracking-wider font-extrabold rounded text-center transition-all ${
                  isActive ? 'bg-[#f59e0b] text-black shadow-inner' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {tier}
              </button>
            );
          })}
        </div>

        {/* Talent Category Scroller list */}
        <div className="flex-1 overflow-y-auto max-h-[50vh] md:max-h-[58vh] xl:max-h-[62vh] pr-2 custom-scrollbar space-y-1.5">
          {filteredTalents.map((t) => {
            const isActive = activeTalentId === t.id;
            return (
              <button
                key={`talent-tab-${t.id}`}
                onClick={() => setActiveTalentId(t.id as number)}
                className={`w-full text-left p-3 rounded-lg border text-xs font-mono transition-all relative overflow-hidden group flex flex-col justify-between ${
                  isActive 
                    ? 'bg-slate-950 border-[#f59e0b] text-white shadow-[inset_0_0_12px_rgba(245,158,11,0.06)]' 
                    : 'bg-black/50 border-slate-900 text-slate-400 hover:border-slate-800 hover:text-slate-200'
                }`}
              >
                {/* Active glow side pill */}
                {isActive && (
                  <div className="absolute top-0 bottom-0 left-0 w-1 bg-[#f59e0b]"></div>
                )}
                
                <div className="flex items-center justify-between gap-1 w-full">
                  <span className="font-extrabold truncate uppercase pr-2 group-hover:text-[#f59e0b] transition-colors">
                    {t.name}
                  </span>
                  <span className="text-[8px] scale-90 px-1 py-0.5 rounded bg-slate-900 border border-slate-800 text-[#f59e0b] shrink-0 font-bold">
                    NODE {t.id}
                  </span>
                </div>

                <div className="flex items-center justify-between text-[8px] text-slate-500 mt-1 uppercase tracking-widest font-bold">
                  <span className="truncate pr-1">{t.grade}</span>
                  <span className="text-yellow-600 truncate">{t.clearanceTime}</span>
                </div>
              </button>
            );
          })}

          {filteredTalents.length === 0 && (
            <div className="text-center py-8 text-slate-600 font-mono text-[10px] uppercase">
              No nodes match strategy filters.
            </div>
          )}
        </div>
      </div>

      {/* Main Panel: Interactive Strategy Area */}
      <div className="flex-1 flex flex-col space-y-6 relative z-10">
        
        {/* Dynamic header cards */}
        <div className="p-6 bg-gradient-to-r from-slate-950 to-black ring-1 ring-slate-800/80 rounded-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-3 flex flex-col items-end gap-1.5">
            <span className="text-[9px] font-mono font-bold text-[#f59e0b] uppercase bg-amber-950/40 border border-amber-900/30 px-2 py-0.5 rounded shadow-sm">
              NEXORAX COMPLIANCE APPROVED
            </span>
            <span className="text-[8px] font-mono text-slate-500">
              ID: NX-TALENT-0887095-{activeTalent.id}
            </span>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500 font-mono text-[9px] uppercase tracking-widest">
              <Award className="w-3.5 h-3.5 text-[#f59e0b]" /> 
              <span>{activeTalent.categoryTitle}</span>
            </div>
            
            <h2 className="text-xl md:text-2xl font-sans font-black text-white uppercase tracking-wider">
              {activeTalent.name}
            </h2>
            <p className="text-[10px] font-mono text-[#f59e0b] uppercase font-black tracking-widest mt-1">
              STATUS: {activeTalent.grade}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-900 font-mono">
            <div className="bg-black/50 p-3 rounded-lg border border-slate-900">
              <span className="block text-[8px] text-slate-500 uppercase tracking-widest mb-1">NEXORAX COMPENSATION</span>
              <span className="text-sm font-black text-white">{activeTalent.salary}</span>
            </div>
            <div className="bg-black/50 p-3 rounded-lg border border-slate-900">
              <span className="block text-[8px] text-slate-500 uppercase tracking-widest mb-1">MANDATOR FUNDING SECURE</span>
              <span className="text-[10px] font-extrabold text-[#f59e0b] truncate block">{activeTalent.fundingStatus}</span>
            </div>
            <div className="bg-black/50 p-3 rounded-lg border border-slate-900">
              <span className="block text-[8px] text-slate-500 uppercase tracking-widest mb-1">CLEARANCE SLA TIME</span>
              <span className="text-sm font-black text-[#50e3c2]">{activeTalent.clearanceTime}</span>
            </div>
          </div>
        </div>

        {/* Tactical Onboarding Workflows case studies */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Work strategy and outcomes */}
          <div className="p-6 bg-slate-950/80 border border-slate-900 rounded-2xl space-y-4">
            <div className="flex items-center gap-1.5 text-xs text-[#f59e0b] font-mono font-black uppercase tracking-wider">
              <Target className="w-4 h-4 text-[#f59e0b]" />
              <span>ACQUISITION MISSION DIRECTIVE</span>
            </div>

            <div className="space-y-4 font-sans leading-relaxed text-xs text-slate-300">
              <p>{activeTalent.strategy}</p>
              <p className="border-l-2 border-yellow-600 pl-3 italic text-slate-400">
                "Subject operates under a zero-cost secure digital protocol. Standard placement logistics are secured using our business development pipelines."
              </p>
            </div>

            <div className="pt-4 border-t border-slate-800">
              <span className="block font-mono text-[8.5px] text-slate-500 uppercase tracking-widest mb-2 font-bold">NEXORAX SUB-NODES COMMANDED:</span>
              <div className="flex flex-wrap gap-1.5">
                {activeTalent.nodes.slice(0, 8).map((node, i) => (
                  <span key={`subnode-tag-${i}`} className="bg-black border border-slate-900 px-2 py-1 rounded text-[9.5px] text-slate-400 font-mono">
                    {node}
                  </span>
                ))}
                {activeTalent.nodes.length > 8 && (
                  <span className="bg-amber-950/40 border border-amber-900/60 px-2 py-1 rounded text-[9.5px] text-[#f59e0b] font-mono">
                    +{activeTalent.nodes.length - 8} MORE NODES
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Standard Compliance and Onboarding validation node */}
          <div className="p-6 bg-[#03060f]/90 border border-slate-900 rounded-2xl flex flex-col justify-between">
            <div className="space-y-4 font-mono">
              <div className="flex items-center justify-between border-b border-slate-900 pb-2">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest font-extrabold flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5 text-blue-500" /> ONBOARDING FRAMEWORK
                </span>
                <span className="text-[9px] text-[#f59e0b] font-bold">COMMERCIAL COMPLIANCE CHECKED</span>
              </div>

              <div className="space-y-3 pt-2 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-400">ONBOARDING STRATEGY</span>
                  <span className="text-white font-extrabold">VIP ACCESS PROTOCOL</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">LEGAL COMPLIANCE REVIEW</span>
                  <span className="text-[#a855f7] font-bold">PENDING ACCOUNTANT/LEGAL AUDIT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">NATIONAL INSURANCE COMPLIANCE</span>
                  <span className="text-yellow-500 font-bold font-mono">Verified Secure Credentials</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 text-left">FISCAL ALLOCATION STATUS</span>
                  <span className="text-[#f59e0b] font-bold text-right font-mono uppercase">COMMERCIAL READY</span>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <button 
                onClick={() => handleTriggerDirectInterview(activeTalent)}
                className="w-full bg-[#f59e0b] hover:bg-amber-400 text-black py-2.5 rounded xl font-mono text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-[0_0_15px_rgba(245,158,11,0.1)] hover:shadow-[0_0_20px_rgba(245,158,11,0.3)] cursor-pointer"
              >
                <span>DEPLOY NEXORAX INTERVIEW</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              
              <div className="flex items-center justify-center gap-4 text-[9px] font-mono text-slate-500 mt-2">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-[#f59e0b]" /> 08000 622827</span>
                <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-[#f59e0b]" /> support@nexoraxdigital.co.uk</span>
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* Persistent Beautiful Responsive Interactive Chat Box */}
      <div className="w-full md:w-80 border border-slate-800 rounded-2xl bg-slate-950 flex flex-col h-[400px] md:h-auto overflow-hidden shrink-0 relative z-10">
        <div className="p-4 bg-gradient-to-r from-slate-900 to-slate-950 border-b border-slate-800 flex items-center justify-between shadow-sm shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#f59e0b] animate-ping"></span>
            <span className="text-[10px] font-mono font-black text-white uppercase tracking-widest">NEXORAX CHAT CORES</span>
          </div>
          <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest font-extrabold">NODE-07 ACTIVE</span>
        </div>

        {/* Message Stream */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 font-mono text-[10px] leading-relaxed custom-scrollbar bg-black/60">
          {chatMessages.map((msg, i) => (
            <div 
              key={`chat-msg-${i}`} 
              className={`flex flex-col max-w-[85%] ${msg.sender === 'user' ? 'ml-auto items-end' : 'mr-auto items-start'}`}
            >
              <span className="text-[7px] text-slate-500 mb-0.5">{msg.time}</span>
              <div className={`p-2.5 rounded-xl border ${
                msg.sender === 'user' 
                  ? 'bg-amber-950/20 border-amber-900/50 text-[#f59e0b]' 
                  : 'bg-slate-900 border-slate-800 text-slate-200'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex items-center gap-1 text-[8.5px] text-slate-500 italic mt-2">
              <RefreshCw className="w-3 h-3 animate-spin text-[#f59e0b]" />
              <span>Nexorax Neural Core routing response...</span>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <form onSubmit={handleSendChat} className="p-3 bg-slate-950 border-t border-slate-900 flex gap-2 shrink-0">
          <input 
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Query credentials or node status..."
            className="flex-1 bg-black text-xs font-mono px-3 py-1.5 rounded-lg border border-slate-800 text-slate-200 focus:outline-none focus:border-amber-600 focus:ring-0 placeholder-slate-700"
          />
          <button 
            type="submit"
            className="p-1.5 bg-[#f59e0b] hover:bg-amber-400 text-black rounded-lg transition-colors cursor-pointer"
            aria-label="Send query"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>

    </div>
  );
};
